import { MEDIAURL } from '../../utilities/constants';
import { getTeamTricode } from './utils';
import styles from './SessionSetup.module.css';

export function RotationsSection({
  desiredSquads,
  desiredRotations,
  desiredEvents,
  currentSquads,
  currentRotations,
  sessionRosters,
}) {
  // Build squad-to-rosters map with tricodes, logos, and colors
  const rostersBySquad = {};
  for (const sr of sessionRosters) {
    if (sr.squadId) {
      const squad = currentSquads.find((s) => s.id === sr.squadId);
      if (squad) {
        if (!rostersBySquad[squad.name]) rostersBySquad[squad.name] = [];
        const team = sr.roster?.team;
        const teamLogo = team?.logos
          ? JSON.parse(team.logos)?.metaData?.filename
          : null;
        const teamColors = team?.colors ? JSON.parse(team.colors) : [];
        rostersBySquad[squad.name].push({
          ...sr,
          tricode: team?.triCode || getTeamTricode(team?.name),
          logo: teamLogo,
          primaryColor: teamColors[0] || '#6b7280',
        });
      }
    }
  }

  // Build a map of actual stages: stagesByRotationOrder[rotationOrder][apparatus] = stage
  const stagesByRotationOrder = {};
  for (const rotation of currentRotations) {
    const stages = rotation.stages?.items?.filter((s) => !s._deleted) || [];
    stagesByRotationOrder[rotation.order] = {};
    for (const stage of stages) {
      stagesByRotationOrder[rotation.order][stage.apparatus] = stage;
    }
  }

  // Count actual vs expected
  const totalActualStages = currentRotations.reduce((count, r) => {
    return count + (r.stages?.items?.filter((s) => !s._deleted)?.length || 0);
  }, 0);
  // Each rotation has one stage per squad (not per event)
  const expectedStages = desiredRotations.length * desiredSquads.length;

  // Use desiredEvents for stable apparatus columns
  const apparatusList = desiredEvents || [];

  return (
    <div>
      {/* Preview info with stage status */}
      <div className={styles.rotationInfo}>
        {currentRotations.length}/{desiredRotations.length} rotations ·{' '}
        {currentSquads.length}/{desiredSquads.length} squads ·{' '}
        <span
          className={
            totalActualStages < expectedStages ? styles.stageWarning : ''
          }
        >
          {totalActualStages}/{expectedStages} stages
        </span>
      </div>

      {desiredRotations.length > 0 && (
        <div
          className={styles.rotationTable}
          style={{
            gridTemplateColumns: `32px repeat(${apparatusList.length}, minmax(90px, 1fr))`,
          }}
        >
          {/* Header row with apparatus */}
          <div className={styles.rotationHeaderRow}>
            <div className={styles.rotationLabelHeader}></div>
            {apparatusList.map((apparatus) => (
              <div key={apparatus} className={styles.apparatusHeader}>
                {apparatus}
              </div>
            ))}
          </div>

          {/* Data rows - each rotation */}
          {desiredRotations.map((rotation, rotationIndex) => {
            const actualRotation = currentRotations.find(
              (r) => r.order === rotationIndex
            );
            const rotationExists = !!actualRotation;

            return (
              <div
                key={rotationIndex}
                className={`${styles.rotationDataRow} ${
                  !rotationExists ? styles.rotationMissing : ''
                }`}
              >
                <div className={styles.rotationLabel}>
                  {rotationIndex + 1}
                  {!rotationExists && (
                    <span className={styles.missingIndicator}>!</span>
                  )}
                </div>
                {apparatusList.map((apparatus) => {
                  const squadName = rotation[apparatus];
                  if (!squadName) {
                    return (
                      <div key={apparatus} className={styles.squadCell}>
                        <div className={styles.squadLetter}>—</div>
                      </div>
                    );
                  }

                  const stageExists =
                    stagesByRotationOrder[rotationIndex]?.[apparatus];
                  const rosters = (rostersBySquad[squadName] || []).sort(
                    (a, b) =>
                      (a.squadStartPosition ?? 0) - (b.squadStartPosition ?? 0)
                  );

                  return (
                    <div
                      key={apparatus}
                      className={`${styles.squadCell} ${
                        !stageExists ? styles.stageMissing : ''
                      }`}
                    >
                      <div className={styles.squadLetter}>
                        {squadName}
                        {!stageExists && (
                          <span className={styles.missingIndicator}>!</span>
                        )}
                      </div>
                      {rosters.length > 0 && (
                        <div className={styles.squadTeams}>
                          {rosters.map((sr) => (
                            <span
                              key={sr.id}
                              className={styles.teamChip}
                              style={{
                                backgroundColor: sr.primaryColor,
                                color: '#fff',
                              }}
                            >
                              {sr.logo && (
                                <img
                                  src={`${MEDIAURL}${sr.logo}`}
                                  alt=""
                                  className={styles.teamChipLogo}
                                />
                              )}
                              {sr.tricode}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {desiredRotations.length === 0 && (
        <div className="text-muted text-center py-2 small">
          Select MULTI type to configure rotations.
        </div>
      )}
    </div>
  );
}
